import { GROK_WORKER_URL } from '../config.js';
import { fetchCache } from '../utils/fetchCache.js';

const sessionCache = new Map();

export const VOICE_PREVIEW_LINE_COUNT = 6;

export const randomPreviewIndex = (lineCount = VOICE_PREVIEW_LINE_COUNT) =>
  Math.floor(Math.random() * lineCount);

const previewCacheKey = (voiceId, language, previewIndex) =>
  `${voiceId}:${language}:${previewIndex}`;

export const nextPreviewIndex = (previewIndex, previewLineCount) => {
  const lineCount = previewLineCount || 1;
  const resolved = Number.isFinite(previewIndex) ? previewIndex : 0;
  return (resolved + 1) % lineCount;
};

const decodePreviewAudio = (base64Audio) => {
  const bytes = Uint8Array.from(atob(base64Audio), (c) => c.charCodeAt(0));
  return URL.createObjectURL(new Blob([bytes], { type: 'audio/mpeg' }));
};

export const fetchVoicePreview = async ({
  voiceId,
  previewIndex = 0,
  language = 'en',
} = {}) => {
  if (!voiceId) throw new Error('voice_id is required');

  const cacheKey = previewCacheKey(voiceId, language, previewIndex);
  const cached = sessionCache.get(cacheKey);
  if (cached) return cached;

  const headers = { 'Content-Type': 'application/json' };
  const authToken = await window.tokenManager?.getValidAccessToken?.() ?? '';
  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  const data = await fetchCache.fetchJSON(`${GROK_WORKER_URL}/preview-voice`, {
    method: 'POST',
    headers,
    body: { voice_id: voiceId, language, preview_index: previewIndex },
  });
  if (!data.audio) throw new Error('Preview response missing audio');

  const previewLineCount = data.preview_line_count || 1;
  const resolvedIndex = Number.isFinite(data.preview_index) ? data.preview_index : previewIndex;
  const result = {
    audio: data.audio,
    objectUrl: decodePreviewAudio(data.audio),
    previewIndex: resolvedIndex,
    previewLineCount,
    nextIndex: nextPreviewIndex(resolvedIndex, previewLineCount),
  };
  sessionCache.set(cacheKey, result);
  return result;
};

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    sessionCache.forEach((entry) => {
      if (entry?.objectUrl) URL.revokeObjectURL(entry.objectUrl);
    });
    sessionCache.clear();
  });
}
